import { useCallback, useState } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import {
    Button,
    type ButtonColorAgainst
} from "@/libraries/design/components/button/src/button.tsx";
import { wacToken } from "@/libraries/design/foundations";
import { DarkThemeProvider } from "@/libraries/design/utils/dark-theme-provider";

const containerStyles = css({
    display: "flex",
    gap: token("space.200"),
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    maxWidth: "808px",
    [media.above.sm]: {
        display: "grid",
        gap: 0,
        gridTemplateColumns: "1fr 32px max-content"
    }
});

const textContainerStyles = css({
    position: "relative",
    gridColumn: "1 / 3",
    gridRow: "1 / 2",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    borderRadius: wacToken("corner.radius.pill"),
    backgroundColor: wacToken("color.neutral.dark200"),
    border: `1px solid ${wacToken("color.neutral.dark500")}`,
    minHeight: "36px",
    display: "flex",
    alignItems: "center",
    padding: "0 16px",
    boxSizing: "border-box"
});

const textStyles = css({
    "margin": 0,
    "whiteSpace": "nowrap",
    "overflow": "hidden",
    "fontFamily": token("font.family.code"),
    "fontSize": "16px",
    "lineHeight": "24px",
    "color": wacToken("color.neutral.dark900"),
    "::after": {
        content: `""`,
        position: "absolute",
        insetBlockStart: 0,
        insetInlineEnd: "16px",
        insetBlockEnd: 0,
        width: "80px",
        background: `linear-gradient(to right, transparent, ${wacToken(
            "color.neutral.dark200"
        )})`,
        pointerEvents: "none"
    }
});

const buttonContainerStyles = css({
    gridColumn: "2 / 4",
    gridRow: "1 / 2",
    zIndex: 1,
    backgroundColor: wacToken("color.neutral.dark100"),
    borderRadius: wacToken("corner.radius.pill")
});

export interface CopyTextProps {
    /** The text content to be copied to clipboard */
    text: string;
    /** The label for the copy button */
    buttonText?: string;
    /** The text shown on the button after a successful copy */
    copiedText?: string;
    /** The aria-label applied to the button after a successful copy */
    copiedAriaLabel?: string;
    /** The color context for the secondary button styling */
    colorAgainst?: ButtonColorAgainst;
}

export const CopyText = ({
    text,
    buttonText = "Copy code",
    copiedText = "Copied!",
    copiedAriaLabel = "Copied to clipboard",
    colorAgainst = "black"
}: CopyTextProps) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(async () => {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [text]);

    return (
        <DarkThemeProvider enabled={true}>
            <div css={containerStyles}>
                <div css={textContainerStyles}>
                    <p css={textStyles}>{text}</p>
                </div>
                <div css={buttonContainerStyles}>
                    <Button
                        variant="secondary"
                        size="large"
                        colorAgainst={colorAgainst}
                        onClick={handleCopy}
                        dataEventProps={{
                            "data-product-key": "uniform",
                            "data-event-component": "linkButton",
                            "data-event-container": "copyText",
                            "data-event": "clicked"
                        }}
                        aria-label={copied ? copiedAriaLabel : buttonText}
                    >
                        {copied ? copiedText : buttonText}
                    </Button>
                </div>
            </div>
        </DarkThemeProvider>
    );
};

export default CopyText;
